/**
 * Analytics Provider Registry
 * Manages registration and lifecycle of analytics providers
 */

import type { AnalyticsProvider } from "@artha/types";

export class AnalyticsRegistry {
  private providers: Map<string, AnalyticsProvider> = new Map();
  private initialized = false;

  /**
   * Register a new analytics provider
   */
  register(provider: AnalyticsProvider): void {
    if (this.providers.has(provider.name)) {
      console.warn(`Analytics provider '${provider.name}' is already registered`);
      return;
    }

    this.providers.set(provider.name, provider);
  }

  /**
   * Unregister an analytics provider
   */
  unregister(providerName: string): void {
    const provider = this.providers.get(providerName);
    if (provider) {
      this.providers.delete(providerName);
    }
  }

  /**
   * Get all registered providers
   */
  getProviders(): AnalyticsProvider[] {
    return Array.from(this.providers.values()).filter(provider => provider.enabled);
  }

  /**
   * Get a specific provider by name
   */
  getProvider(name: string): AnalyticsProvider | undefined {
    return this.providers.get(name);
  }

  /**
   * Get all provider names
   */
  getProviderNames(): string[] {
    return Array.from(this.providers.keys());
  }

  /**
   * Initialize all registered providers
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    const providers = this.getProviders();
    const initPromises = providers.map(async (provider) => {
      try {
        if (provider.initialize) {
          await provider.initialize();
        }
      } catch (error) {
        console.error(`Failed to initialize analytics provider '${provider.name}':`, error);
      }
    });

    await Promise.allSettled(initPromises);
    this.initialized = true;
  }

  /**
   * Destroy all registered providers
   */
  async destroy(): Promise<void> {
    const providers = Array.from(this.providers.values());
    const destroyPromises = providers.map(async (provider) => {
      try {
        if (provider.destroy) {
          await provider.destroy();
        }
      } catch (error) {
        console.error(`Failed to destroy analytics provider '${provider.name}':`, error);
      }
    });

    await Promise.allSettled(destroyPromises);
    this.providers.clear();
    this.initialized = false;
  }

  /**
   * Check if registry is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get enabled provider count
   */
  getEnabledProviderCount(): number {
    return this.getProviders().length;
  }
}